import { ReactComponent as CheckOn } from '@/images/check_on.svg';
import { BankCardTagType, BankEddState, DefaultType, EddaBankInfo } from '@/interface/KKC/edda';
import classNames from 'classnames';
import { t } from 'i18next';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import VCreditCheckbox from '../VCreditCheckbox';
import infoStyle from './bankinfocard.module.less';
import BankCardTag from './components/BankCardTag';
import { getHashNum, numPrimaryColor } from './map';

export enum BankInfoCardStatus {
  DEFAULT, // 展示狀態
  MANAGEMENT, // 管理狀態
  SELECT, // 选择状态
}

export enum Useage {
  EddaList,
}

interface BankInfoCardProps {
  data: EddaBankInfo;
  style?: CSSProperties;
  status?: BankInfoCardStatus;
  onSetDefault?: (data: EddaBankInfo) => void;
  onUnbind?: (data: EddaBankInfo) => void;
  onSetNotDefault?: (data: EddaBankInfo) => void;
  onCheck?: (data: EddaBankInfo) => void;
  usage?: Useage; // 该卡片用在什么位置，有相应的不同展示
  checked?: boolean; // 使用选择状态时，是否为选中状态
}

function BankInfoCard(props: BankInfoCardProps) {
  const {
    data: {
      bankCardName,
      bankCardNumShow,
      bankCardEnName,
      isDefault,
      state = BankEddState.BindSuccess,
      bankCardCode = '',
      defaultmesgId,
      msgId,
    },
    data,
    style,
    status = BankInfoCardStatus.DEFAULT,
    onSetDefault,
    onSetNotDefault,
    onUnbind,
    onCheck,
    usage,
    checked,
  } = props;
  const layoutRef = useRef<HTMLDivElement>(null);
  const [tag, setTag] = useState<BankCardTagType>();

  const styleIndex = getHashNum(bankCardCode);

  useEffect(() => {
    layoutRef.current?.style.setProperty('--card_primarycolor', numPrimaryColor[styleIndex]);
    layoutRef.current?.style.setProperty(
      '--card_border_primarycolor',
      `${numPrimaryColor[styleIndex]}40`
    );
  }, [bankCardEnName]);

  useEffect(() => {
    if (isDefault === DefaultType.NotDefault) {
      // 清除原有的tag状态
      setTag(BankCardTagType.NotDefault);
    }
    if (state === BankEddState.Unbinding) {
      setTag(BankCardTagType.UNBINGDING);
    }
    if (state === BankEddState.BindHandling) {
      setTag(BankCardTagType.HANDLING);
    }
    if (isDefault === DefaultType.Default) {
      setTag(BankCardTagType.DEFAULT);
    }
  }, [state, isDefault]);

  const onDefaultCheck = () => {
    if (isDefault !== DefaultType.Default) {
      // 如果不等于默认的情况下 就会输入出去开始遍历数据让当前变成默认
      onSetDefault && onSetDefault(data);
    }
    if (isDefault === DefaultType.Default) {
      // 如果默认的情况下 就会输入出去开始遍历数据让当前数据变成不默认
      onSetNotDefault && onSetNotDefault(data);
    }
  };

  const onClickUnbind = () => {
    if (isDefault !== DefaultType.Default) {
      onUnbind && onUnbind(data);
    }
  };

  const onCheckChange = (checkVal: boolean) => {
    if (checkVal && onCheck) {
      onCheck(data);
    }
  };

  return (
    <div ref={layoutRef} className={infoStyle.outerLayout}>
      <div
        className={infoStyle.layout}
        style={{
          ...style,
        }}
      >
        <div className={infoStyle.bg_text}>{bankCardEnName}</div>
        <div
          className={classNames({
            [infoStyle.bankname_wrapper]: true,
            [infoStyle.bankname_wrapper_between]: status === BankInfoCardStatus.SELECT,
          })}
        >
          <div className={infoStyle.bankname}>
            <span>{bankCardName}</span>
          </div>
          {[BankInfoCardStatus.DEFAULT, BankInfoCardStatus.MANAGEMENT].includes(status) ? (
            <BankCardTag tag={tag} className={infoStyle.bankTag} />
          ) : undefined}
          {status === BankInfoCardStatus.SELECT ? (
            <VCreditCheckbox iconSize={16} checked={checked} onChange={onCheckChange} />
          ) : undefined}
        </div>
        <div className={infoStyle.bank_card_title}>{t('銀行賬戶號碼')}</div>
        <div className={infoStyle.bank_card}>{bankCardNumShow?.replace(/• /g, '･')}</div>
      </div>
      {status === BankInfoCardStatus.MANAGEMENT &&
      ![BankEddState.Unbinding, BankEddState.BindHandling].includes(state) ? (
        <div className={infoStyle.bottomlayout}>
          <div className={infoStyle.default_check_wrapper} onClick={onDefaultCheck}>
            {isDefault === DefaultType.Default ? (
              <CheckOn className={infoStyle.default_check} />
            ) : (
              <div className={infoStyle.hollow_circle} />
            )}
            <span>{t('設為預設還款賬戶')}</span>
          </div>
          {isDefault !== DefaultType.Default && defaultmesgId !== msgId ? (
            <div className={infoStyle.unbindtext} onClick={onClickUnbind}>
              {t('edda.manage.delink')}
            </div>
          ) : (
            <div />
          )}
        </div>
      ) : null}
      {usage === Useage.EddaList && status === BankInfoCardStatus.DEFAULT ? (
        state === BankEddState.BindHandling ? (
          <div className={infoStyle.hint}>{t('edda.bankcard.handling_hint')}</div>
        ) : state === BankEddState.Unbinding ? (
          <div className={infoStyle.hint}>{t('edda.bankcard.unbinding_hint')}</div>
        ) : (
          <div />
        )
      ) : (
        <div />
      )}
    </div>
  );
}

BankInfoCard.defaultProps = {
  style: {},
  status: BankInfoCardStatus.MANAGEMENT,
  usage: Useage.EddaList,
};

export default BankInfoCard;
